/**
 * Canonical password policy for user-chosen passwords (invitation acceptance,
 * password changes, etc.).
 *
 * ⚠️ This MUST stay in sync with the Cognito user pool password policy defined
 * in Terraform (`infra/.../cognito` → `password_policy`). Drift is a real,
 * user-visible bug: if the app accepts a password Cognito then rejects, the
 * `AdminCreateUser`/`AdminSetUserPassword` call throws `InvalidPasswordException`
 * and the invitee is left permanently stuck at "not accepted" because
 * `employees.user_id` is never written. Both the frontend form and the backend
 * Zod schema import from here so they can't diverge from each other — keep this
 * matched to the Cognito policy whenever either changes.
 */
export const PASSWORD_POLICY = {
  /** Cognito `minimum_length`. */
  minLength: 12,
  /**
   * bcrypt-era cap carried over from the original accept schema. Well under
   * Cognito's 256 max; kept so overly long inputs fail fast with a friendly
   * message rather than at the SDK boundary.
   */
  maxLength: 72,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbols: true,
} as const;

/**
 * Characters Cognito counts as symbols for `require_symbols`. Mirrors the set
 * documented in the AWS Cognito user pool password policy (includes space).
 */
const PASSWORD_SYMBOL_REGEX = /[ ^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]/;

/**
 * Validate a password against {@link PASSWORD_POLICY}. Returns the first
 * violation message (suitable for showing to the user), or `null` when the
 * password is compliant.
 *
 * Order of checks (length → character classes) matches the messages users
 * expect and keeps a single "first problem" surfaced at a time.
 */
export function validatePasswordPolicy(
  value: string | null | undefined,
): string | null {
  if (!value) return "Password is required";

  if (value.length < PASSWORD_POLICY.minLength) {
    return `Password must be at least ${PASSWORD_POLICY.minLength} characters`;
  }
  if (value.length > PASSWORD_POLICY.maxLength) {
    return `Password must be less than ${PASSWORD_POLICY.maxLength} characters`;
  }
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(value)) {
    return "Password must contain at least one uppercase letter";
  }
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(value)) {
    return "Password must contain at least one lowercase letter";
  }
  if (PASSWORD_POLICY.requireNumbers && !/[0-9]/.test(value)) {
    return "Password must contain at least one number";
  }
  if (PASSWORD_POLICY.requireSymbols && !PASSWORD_SYMBOL_REGEX.test(value)) {
    return "Password must contain at least one symbol";
  }
  return null;
}

/** Human-readable summary of the policy for form hint/description text. */
export const PASSWORD_POLICY_DESCRIPTION =
  "Must be at least 12 characters with uppercase, lowercase, number, and symbol";
